<template>
  <div class="dynamic-translations-editor">
    <!-- Translation Keys List -->
    <div v-if="translationKeys.length > 0" class="translation-keys-list mb-3">
      <div
          v-for="(key, index) in translationKeys"
          :key="index"
          class="translation-key-item mb-3 p-3 border rounded"
      >
        <CRow class="align-items-center mb-2">
          <CCol col>
            <strong v-translate translate-context="event_wizard.email_schedule.translations">Key:</strong>
            {{ key }}
          </CCol>
          <CCol col="auto">
            <CButton
                color="danger"
                size="sm"
                variant="ghost"
                @click="removeKey(key)"
            >
              <CIcon name="cil-trash" />
            </CButton>
          </CCol>
        </CRow>
        <CRow>
          <CCol col="12">
            <PTranslatableTextInput
                :value="getTranslationsForKey(key)"
                :placeholder="$pgettext('event_wizard.email_schedule.translations', 'Enter translation value')"
                @input="(value) => onTranslationChange(key, value)"
            />
            <!-- Validation Errors for invalid placeholders -->
            <div v-if="getInvalidPlaceholders(key).length > 0" class="invalid-placeholders mt-2">
              <small class="text-danger">
                <CIcon name="cilWarning" size="sm" class="mr-1"/>
                <translate translate-context="event_wizard.email_schedule.translations">Invalid parameters:</translate>
                <code v-for="(param, idx) in getInvalidPlaceholders(key)" :key="param" class="mx-1">
                  {<span>{{ param }}</span>}<span v-if="idx < getInvalidPlaceholders(key).length - 1">,</span>
                </code>
              </small>
            </div>
          </CCol>
        </CRow>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state text-center text-muted py-4">
      <p v-translate translate-context="event_wizard.email_schedule.translations">
        No translation keys defined yet. Click the "+" button below to add your first key.
      </p>
    </div>

    <!-- Add New Key Section -->
    <CRow class="align-items-end">
      <CCol col>
        <label v-translate translate-context="event_wizard.email_schedule.translations">
          New Translation Key
        </label>
        <CInput
            v-model="newKeyName"
            :placeholder="$pgettext('event_wizard.email_schedule.translations', 'e.g., header, footer, button_text')"
            @keyup.enter="addKey"
        />
        <small class="text-muted">
          <translate translate-context="event_wizard.email_schedule.translations">
            Use lowercase letters, numbers, and underscores only (e.g., header_text, button_label)
          </translate>
        </small>
      </CCol>
      <CCol col="auto">
        <CButton
            color="primary"
            @click="addKey"
            :disabled="!isValidKeyName"
        >
          <CIcon name="cil-plus" />
          <translate translate-context="event_wizard.email_schedule.translations">Add Key</translate>
        </CButton>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import PTranslatableTextInput from '@/domain/core/components/PTranslatableTextInput.vue'

export default {
  name: 'DynamicTranslationsEditor',
  components: {
    PTranslatableTextInput
  },
  props: {
    /**
     * Translations object structure:
     * {
     *   it: {key1: 'valore1', key2: 'valore2'},
     *   en: {key1: 'value1', key2: 'value2'}
     * }
     */
    translations: {
      type: Object,
      default: () => ({})
    },
    /**
     * Available parameters that can be used as placeholders in translations.
     * Array of parameter names (e.g., ['username', 'event_name', 'price'])
     */
    availableParameters: {
      type: Array,
      default: () => ([])
    }
  },
  data() {
    return {
      localTranslations: {},
      newKeyName: '',
      validKeyPattern: /^[a-z0-9_]+$/
    }
  },
  computed: {
    /**
     * Get list of translation keys from the first language
     */
    translationKeys() {
      // Get all unique keys from all languages
      const keys = new Set()
      Object.values(this.localTranslations).forEach(langTranslations => {
        if (langTranslations && typeof langTranslations === 'object') {
          Object.keys(langTranslations).forEach(key => keys.add(key))
        }
      })
      return Array.from(keys).sort()
    },
    /**
     * Validate new key name
     */
    isValidKeyName() {
      return this.newKeyName.length > 0 &&
          this.validKeyPattern.test(this.newKeyName) &&
          !this.translationKeys.includes(this.newKeyName)
    }
  },
  watch: {
    translations: {
      immediate: true,
      deep: true,
      handler(newTranslations) {
        // Deep copy to ensure reactivity and avoid reference issues
        this.localTranslations = JSON.parse(JSON.stringify(newTranslations || {}))
      }
    }
  },
  methods: {
    /**
     * Get translations for a specific key across all languages
     * Returns object like {it: 'valore', en: 'value'}
     */
    getTranslationsForKey(key) {
      const result = {}
      Object.keys(this.localTranslations).forEach(lang => {
        if (this.localTranslations[lang] && this.localTranslations[lang][key]) {
          result[lang] = this.localTranslations[lang][key]
        }
      })
      return result
    },
    /**
     * Update translations for a specific key
     */
    onTranslationChange(key, translationsObj) {
      // translationsObj is like {it: 'valore', en: 'value'}
      Object.keys(translationsObj).forEach(lang => {
        if (!this.localTranslations[lang]) {
          this.$set(this.localTranslations, lang, {})
        }
        this.$set(this.localTranslations[lang], key, translationsObj[lang])
      })
      this.emitUpdate()
    },
    /**
     * Add a new translation key
     */
    addKey() {
      if (!this.isValidKeyName) return

      const key = this.newKeyName.trim()

      // Initialize the key in all existing languages
      const supportedLangs = process.env.VUE_APP_SUPPORTED_LANGS.split(',')
      supportedLangs.forEach(lang => {
        if (!this.localTranslations[lang]) {
          this.$set(this.localTranslations, lang, {})
        }
        if (!this.localTranslations[lang][key]) {
          this.$set(this.localTranslations[lang], key, '')
        }
      })

      this.newKeyName = ''
      this.emitUpdate()
    },
    /**
     * Remove a translation key
     */
    removeKey(key) {
      Object.keys(this.localTranslations).forEach(lang => {
        if (this.localTranslations[lang] && this.localTranslations[lang][key] !== undefined) {
          this.$delete(this.localTranslations[lang], key)
        }
      })
      this.emitUpdate()
    },
    /**
     * Emit update event
     */
    emitUpdate() {
      this.$emit('update', { ...this.localTranslations })
    },
    /**
     * Extract placeholders from a text string
     * Matches {placeholder} syntax
     * @param {string} text
     * @returns {Array<string>} - Array of placeholder names
     */
    extractPlaceholders(text) {
      if (!text) return []
      const matches = text.match(/\{([^}]+)\}/g) || []
      return matches.map(match => match.slice(1, -1))
    },
    /**
     * Get all translation values for a key across all languages
     * @param {string} key
     * @returns {Array<string>}
     */
    getAllTranslationValues(key) {
      const translations = this.getTranslationsForKey(key)
      return Object.values(translations).filter(v => v)
    },
    /**
     * Get invalid placeholders for a translation key (checks all languages)
     * @param {string} key
     * @returns {Array<string>}
     */
    getInvalidPlaceholders(key) {
      const allValues = this.getAllTranslationValues(key)
      const allPlaceholders = new Set()

      allValues.forEach(text => {
        this.extractPlaceholders(text).forEach(p => allPlaceholders.add(p))
      })

      return Array.from(allPlaceholders).filter(p => !this.availableParameters.includes(p))
    }
  }
}
</script>

<style scoped>
.dynamic-translations-editor {
  background-color: #ffffff;
  padding: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #d8dbe0;
}

.translation-key-item {
  background-color: #f8f9fa;
}

.empty-state {
  border: 2px dashed #d8dbe0;
  border-radius: 0.25rem;
}

.invalid-placeholders {
  background-color: #fef2f2;
  padding: 0.35rem 0.5rem;
  border-radius: 0.25rem;
  border-left: 3px solid #e55353;
}

.invalid-placeholders code {
  background-color: #fee2e2;
  color: #dc2626;
  padding: 0.1rem 0.3rem;
  border-radius: 0.2rem;
  font-size: 0.85em;
}
</style>
